import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWaitingListStore } from '../../stores/waitingListStore';
import { Loader2, Clock, X, Mail, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';
import clsx from 'clsx';
import { remainingPlaces } from '../../lib/places';
import InscriptionDepuisAttenteModal, { type InscriptionEnAttente } from '../../components/admin/InscriptionDepuisAttenteModal';
import Telephone from '../../components/admin/Telephone';
import { lireInscriptionsDeLaSemaine } from '../../lib/listeAttente/inscriptions.supabase';
import { sansJpa, stagesDesJpa } from '../../lib/jpa/stages.supabase';
import { situationDeLaSemaine, type InscriptionDeLEnfant, type SituationDeLaSemaine } from '../../lib/listeAttente/semaine';
import { aujourdhuiABruxelles, joursEntre } from '../../lib/tableauDeBord/general';
import {
  comparerSessions,
  dansLePerimetre,
  ecrireFiltres,
  estActive,
  FILTRES_PAR_DEFAUT,
  FILTRES_STATUT,
  lireFiltres,
  optionsDe,
  QUANDS,
  rangsDansLaFile,
  sessionRetenue,
  statutRetenu,
  type Filtres,
  type SessionFiltrable,
} from '../../lib/listeAttente/filtres';

interface SessionListe extends SessionFiltrable {
  capacity: number;
  current_registrations: number;
}

/** Une ligne de `sessions` telle que la requête ci-dessous la rend. */
interface SessionLue {
  id: string;
  start_date: string;
  end_date: string;
  archived: boolean | null;
  center_id: string;
  stage_id: string;
  capacity: number;
  current_registrations: number | null;
  stage: { title: string } | { title: string }[] | null;
  center: { name: string } | { name: string }[] | null;
}

const premier = <T,>(v: T | T[] | null): T | null => (Array.isArray(v) ? v[0] ?? null : v);

const enfants = (n: number) => `${n} ${n > 1 ? 'enfants' : 'enfant'}`;

const dateFr = (d: string) => new Date(d).toLocaleDateString('fr-FR');

/** « En cours », « Commence demain », « Commence dans 9 jours », « Terminée ». */
function quandLaSession(s: SessionListe, aujourdhui: string): string {
  if (s.end_date < aujourdhui) return 'Terminée';
  if (s.start_date <= aujourdhui) return 'En cours';
  const jours = joursEntre(aujourdhui, s.start_date);
  return jours === 1 ? 'Commence demain' : `Commence dans ${jours} jours`;
}

/** Où l'enfant est inscrit la semaine de la session qu'il attend. Voir `situationDeLaSemaine`. */
function SituationSemaine({ situation }: { situation: SituationDeLaSemaine }) {
  const badge = 'mt-1 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset';
  if (situation.cle === 'nulle-part') {
    return <div className={clsx(badge, 'bg-indigo-50 text-indigo-800 ring-indigo-200')}>Inscrit nulle part cette semaine</div>;
  }
  if (situation.cle === 'deja-inscrit') {
    return <div className={clsx(badge, 'bg-gray-100 text-gray-700 ring-gray-200')}>Déjà inscrit à cette session</div>;
  }
  return (
    <div className="mt-1 space-y-1">
      {situation.inscriptions.map((i) => (
        <div key={i.id} className={clsx(badge, 'bg-amber-50 text-amber-900 ring-amber-200')}>
          Inscrit cette semaine : {i.stage} · {i.centre}
          {i.cancellation_status === 'requested' && ' (annulation demandée)'}
        </div>
      ))}
    </div>
  );
}

const AdminWaitingListPage = () => {
  const {
    entries,
    fetchWaitingList,
    isLoading,
    error,
    offerSeat,
    cancelWaitingListEntry
  } = useWaitingListStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const filtres = useMemo(() => lireFiltres(searchParams), [searchParams]);
  const aujourdhui = useMemo(() => aujourdhuiABruxelles(), []);
  const [activities, setActivities] = useState<SessionListe[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isProcessingEntry, setIsProcessingEntry] = useState<string | null>(null);
  const [aInscrire, setAInscrire] = useState<InscriptionEnAttente | null>(null);
  const [inscriptionsSemaine, setInscriptionsSemaine] = useState<InscriptionDeLEnfant[] | null>(null);
  const [inscriptionsIllisibles, setInscriptionsIllisibles] = useState(false);
  const [relectureInscriptions, setRelectureInscriptions] = useState(0);

  useEffect(() => {
    fetchWaitingList();
    fetchActivities();
  }, [fetchWaitingList]);

  const fetchActivities = async () => {
    try {
      // Les filtres de la liste d'attente : les stages seulement (une journée pédagogique n'a pas de liste d'attente).
      const { data, error } = await sansJpa(supabase
        .from('sessions')
        .select(`
          id,
          start_date,
          end_date,
          archived,
          center_id,
          stage_id,
          capacity,
          current_registrations,
          stage:stages(title),
          center:centers(name)
        `)
        .order('start_date', { ascending: false }), await stagesDesJpa());

      if (error) throw error;
      setActivities(((data ?? []) as unknown as SessionLue[]).map((s) => ({
        id: s.id,
        start_date: s.start_date,
        end_date: s.end_date,
        archived: s.archived === true,
        center_id: s.center_id,
        stage_id: s.stage_id,
        centre_nom: premier(s.center)?.name ?? 'Centre inconnu',
        groupe_nom: premier(s.stage)?.title ?? 'Stage inconnu',
        capacity: s.capacity,
        current_registrations: s.current_registrations ?? 0,
      })));
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  /*
    Changer un filtre de session efface la session choisie : elle l'emporte
    sur les autres (voir `sessionRetenue`), et la garder rendrait le nouveau
    filtre sans effet.
  */
  const changerFiltres = (changement: Partial<Filtres>) => {
    const suivants = { ...filtres, ...changement };
    if (!('session' in changement) && !('statut' in changement)) suivants.session = null;
    setSearchParams(ecrireFiltres(suivants), { replace: true });
  };

  const handleOfferSeat = async (id: string) => {
    try {
      setIsProcessingEntry(id);
      await offerSeat(id);
      toast.success('Place offerte avec succès');
    } catch (error) {
      console.error('Error offering seat:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setIsProcessingEntry(null);
    }
  };

  /*
    « Inscrire » ouvre l'inscription par l'administration, déjà remplie :
    parent, enfant et session viennent de la ligne d'attente. Il remplace
    « Convertir en inscription », qui appelait une fonction absente du store
    et échouait à chaque clic.
  */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const ouvrirInscription = (entry: any, session: SessionListe, cetteSemaine: SituationDeLaSemaine | null) => {
    setAInscrire({
      attenteId: entry.id,
      cetteSemaine,
      parent: {
        id: entry.user_id,
        email: entry.parent?.email ?? null,
        prenom: entry.parent?.prenom ?? '',
        nom: entry.parent?.nom ?? '',
        telephone: entry.parent?.telephone ?? null,
      },
      enfant: { id: entry.kid_id, prenom: entry.kid?.prenom ?? '', nom: entry.kid?.nom ?? '' },
      session: {
        id: session.id,
        stage: session.groupe_nom,
        centre: session.centre_nom,
        dates: `${dateFr(session.start_date)} - ${dateFr(session.end_date)}`,
        capacite: session.capacity,
        inscrits: session.current_registrations,
      },
    });
  };

  const handleCancelWaitingList = async (id: string) => {
    try {
      setIsProcessingEntry(id);
      await cancelWaitingListEntry(id);
      toast.success('Entrée annulée avec succès');
    } catch (error) {
      console.error('Error cancelling waiting list entry:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setIsProcessingEntry(null);
    }
  };

  // Le rang se compte sur la file entière de la session, pas sur ce que le filtre de statut montre.
  const rangs = useMemo(() => rangsDansLaFile(entries), [entries]);

  const entreesRetenues = useMemo(
    () => entries.filter((entry) => statutRetenu(entry.status, filtres.statut)),
    [entries, filtres.statut],
  );

  // Les sessions qui ont au moins une ligne au statut retenu, quels que soient les autres filtres.
  const sessionsAvecLignes = useMemo(
    () => new Set(entreesRetenues.map((entry) => entry.activity_id)),
    [entreesRetenues],
  );

  const sessionsAffichees = useMemo(
    () => activities
      .filter((s) => sessionsAvecLignes.has(s.id) && sessionRetenue(s, filtres, aujourdhui))
      .sort((a, b) => comparerSessions(a, b, aujourdhui)),
    [activities, sessionsAvecLignes, filtres, aujourdhui],
  );

  // Group entries by activity
  const entriesByActivity = useMemo(() => {
    const parSession: Record<string, typeof entries> = {};
    for (const entry of entreesRetenues) {
      (parSession[entry.activity_id] ??= []).push(entry);
    }
    return parSession;
  }, [entreesRetenues]);

  /*
    Les enfants encore dans la file, sur les sessions à l'écran, et les dates
    qu'elles couvrent : la lecture de leurs inscriptions s'y borne. La clé
    sérialisée sert de dépendance, comme dans `useDevis` : relire la liste
    d'attente ne relit pas les inscriptions si les mêmes enfants restent.
  */
  const cleInscriptions = useMemo(() => {
    const kidIds = new Set<string>();
    let debut: string | null = null;
    let fin: string | null = null;
    for (const s of sessionsAffichees) {
      const actives = (entriesByActivity[s.id] ?? []).filter((entry) => estActive(entry.status));
      if (actives.length === 0) continue;
      actives.forEach((entry) => kidIds.add(entry.kid_id));
      if (!debut || s.start_date < debut) debut = s.start_date;
      if (!fin || s.end_date > fin) fin = s.end_date;
    }
    return JSON.stringify([[...kidIds].sort(), debut, fin]);
  }, [sessionsAffichees, entriesByActivity]);

  useEffect(() => {
    const [kidIds, debut, fin] = JSON.parse(cleInscriptions) as [string[], string | null, string | null];
    if (kidIds.length === 0 || !debut || !fin) {
      setInscriptionsSemaine([]);
      return;
    }
    let abandonnee = false;
    setInscriptionsSemaine(null);
    setInscriptionsIllisibles(false);
    lireInscriptionsDeLaSemaine(kidIds, debut, fin)
      .then((lues) => { if (!abandonnee) setInscriptionsSemaine(lues); })
      .catch((err) => {
        console.error('Error fetching registrations of the week:', err);
        // Pas de badge plutôt qu'un « inscrit nulle part » faux.
        if (!abandonnee) setInscriptionsIllisibles(true);
      });
    return () => { abandonnee = true; };
  }, [cleInscriptions, relectureInscriptions]);

  const inscriptionsParEnfant = useMemo(() => {
    const parEnfant = new Map<string, InscriptionDeLEnfant[]>();
    for (const i of inscriptionsSemaine ?? []) {
      const liste = parEnfant.get(i.kid_id) ?? [];
      liste.push(i);
      parEnfant.set(i.kid_id, liste);
    }
    return parEnfant;
  }, [inscriptionsSemaine]);

  // Seuls les enfants encore dans la file ont une situation : c'est pour eux qu'on appelle.
  const situationDe = (entry: { kid_id: string; status: string }, session: SessionListe) =>
    inscriptionsSemaine && estActive(entry.status)
      ? situationDeLaSemaine(session, entry.kid_id, inscriptionsParEnfant.get(entry.kid_id) ?? [])
      : null;

  const nombreAffiche = sessionsAffichees.reduce(
    (total, s) => total + (entriesByActivity[s.id]?.length ?? 0),
    0,
  );

  const perimetre = (s: SessionFiltrable) => dansLePerimetre(s, filtres, aujourdhui);
  const optionsCentre = optionsDe(activities, 'centre', perimetre, filtres.centre);
  const optionsGroupe = optionsDe(
    activities,
    'groupe',
    (s) => perimetre(s) && (!filtres.centre || s.center_id === filtres.centre),
    filtres.groupe,
  );

  // Le choix d'une session suit les autres filtres ; la session choisie y reste toujours.
  const optionsSession = activities
    .filter((s) =>
      s.id === filtres.session ||
      (sessionsAvecLignes.has(s.id) && sessionRetenue(s, { ...filtres, session: null }, aujourdhui)))
    .sort((a, b) => comparerSessions(a, b, aujourdhui));

  const filtresModifies = ecrireFiltres(filtres).toString() !== ecrireFiltres(FILTRES_PAR_DEFAUT).toString();

  const getActivityName = (session: SessionListe) =>
    `${session.groupe_nom} - ${dateFr(session.start_date)} au ${dateFr(session.end_date)} - Centre: ${session.centre_nom}`;

  const invitationExpiree = (entry: { status: string; expires_at: string | null }) =>
    entry.status === 'invited' && entry.expires_at !== null && new Date(entry.expires_at) < new Date();

  const getWaitingListStatusColor = (entry: { status: string; expires_at: string | null }) => {
    if (invitationExpiree(entry)) return 'bg-red-100 text-red-800';
    switch (entry.status) {
      case 'invited':
        return 'bg-blue-100 text-blue-800';
      case 'converted':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      case 'waiting':
      default:
        return 'bg-amber-100 text-amber-800';
    }
  };

  const getWaitingListStatusText = (entry: { status: string; expires_at: string | null }) => {
    if (entry.status === 'invited') {
      if (invitationExpiree(entry)) return 'Invitation expirée';
      // Calculate remaining time if expires_at is set
      if (entry.expires_at) {
        const expiresAt = new Date(entry.expires_at);
        const now = new Date();
        const hoursRemaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)));

        return `Invité · ${hoursRemaining} h pour confirmer`;
      }
      return 'Place disponible';
    }
    if (entry.status === 'converted') return 'Converti';
    if (entry.status === 'cancelled') return 'Annulé';
    return 'En attente';
  };

  // Unwrap arrays for kid and parent if needed
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const unwrapEntry = (entry: any) => {
    const kid = Array.isArray(entry.kid) ? entry.kid[0] : entry.kid;
    const parent = Array.isArray(entry.parent) ? entry.parent[0] : entry.parent;
    const session = Array.isArray(entry.session) ? entry.session[0] : entry.session;
    return { ...entry, kid, parent, session };
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Liste d'attente</h1>
          <p className="text-gray-600">Gérez les listes d'attente pour les sessions complètes</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Dates des sessions">
            {QUANDS.map((q) => (
              <button
                key={q.cle}
                type="button"
                onClick={() => changerFiltres({ quand: q.cle })}
                title={q.cle === 'semaine' ? 'Sessions en cours ou qui commencent dans les 7 jours' : undefined}
                aria-pressed={!filtres.session && filtres.quand === q.cle}
                className={clsx(
                  'min-h-[40px] rounded-full px-4 text-sm font-medium ring-1 ring-inset',
                  !filtres.session && filtres.quand === q.cle
                    ? 'bg-primary-600 text-white ring-primary-600'
                    : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50',
                )}
              >
                {q.libelle}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label htmlFor="filtre-centre" className="block text-sm font-medium text-gray-700 mb-2">
                Centre
              </label>
              <select
                id="filtre-centre"
                value={filtres.centre ?? ''}
                onChange={(e) => changerFiltres({ centre: e.target.value || null, groupe: null })}
                className="form-input"
              >
                <option value="">Tous les centres</option>
                {optionsCentre.map((o) => (
                  <option key={o.valeur} value={o.valeur}>{o.libelle}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="filtre-groupe" className="block text-sm font-medium text-gray-700 mb-2">
                Groupe
              </label>
              <select
                id="filtre-groupe"
                value={filtres.groupe ?? ''}
                onChange={(e) => changerFiltres({ groupe: e.target.value || null })}
                className="form-input"
              >
                <option value="">Tous les groupes</option>
                {optionsGroupe.map((o) => (
                  <option key={o.valeur} value={o.valeur}>{o.libelle}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="filtre-statut" className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                id="filtre-statut"
                value={filtres.statut}
                onChange={(e) => changerFiltres({
                  statut: FILTRES_STATUT.find((s) => s.cle === e.target.value)?.cle ?? FILTRES_PAR_DEFAUT.statut,
                })}
                className="form-input"
              >
                {FILTRES_STATUT.map((s) => (
                  <option key={s.cle} value={s.cle}>{s.libelle}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="filtre-session" className="block text-sm font-medium text-gray-700 mb-2">
              Session
            </label>
            <select
              id="filtre-session"
              value={filtres.session ?? ''}
              onChange={(e) => changerFiltres({ session: e.target.value || null })}
              className="form-input"
            >
              <option value="">Toutes les sessions de la sélection</option>
              {optionsSession.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.groupe_nom} - {dateFr(s.start_date)} - {s.centre_nom}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex min-h-[40px] items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filtres.avecArchivees}
                onChange={(e) => changerFiltres({ avecArchivees: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              Inclure les sessions archivées
            </label>
            {filtresModifies && (
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
                className="min-h-[40px] text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>

        {isLoading || isLoadingSessions ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : sessionsAffichees.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucune liste d'attente pour ces filtres</p>
          </div>
        ) : (
          <div className="space-y-8">
            <p className="text-sm text-gray-600">
              {sessionsAffichees.length} {sessionsAffichees.length > 1 ? 'sessions' : 'session'} · {enfants(nombreAffiche)}
            </p>
            {inscriptionsIllisibles && (
              <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                Les autres inscriptions des enfants n'ont pas pu être lues : la page ne dit pas, pour l'instant, s'ils sont déjà inscrits ailleurs cette semaine.
              </p>
            )}
            {sessionsAffichees.map((session) => {
              const lignes = entriesByActivity[session.id] ?? [];
              const libres = remainingPlaces(session);
              const enAttente = lignes.filter((entry) => estActive(entry.status)).length;
              return (
              <div key={session.id} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 p-4 border-b">
                  <h3 className="font-medium">{getActivityName(session)}</h3>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    <span>
                      {enfants(lignes.length)}
                      {filtres.statut === 'actifs'
                        ? ' en attente'
                        : ` · ${FILTRES_STATUT.find((s) => s.cle === filtres.statut)?.libelle.toLowerCase()}`}
                    </span>
                    <span>{quandLaSession(session, aujourdhui)}</span>
                    {session.archived && <span>Archivée</span>}
                    <span
                      className={clsx(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        libres > 0 && enAttente > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700',
                      )}
                    >
                      {session.current_registrations}/{session.capacity} inscrits
                      {libres > 0 ? ` · ${libres} ${libres > 1 ? 'places libres' : 'place libre'}` : ' · complet'}
                    </span>
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          #
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enfant
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Parent
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          Ajouté le
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lignes.map((entry) => {
                        // Unwrap nested objects
                        const unwrappedEntry = unwrapEntry(entry);
                        const rang = rangs.get(unwrappedEntry.id);
                        const situation = situationDe(unwrappedEntry, session);

                        return (
                          <tr key={unwrappedEntry.id} className={clsx(
                            "align-top hover:bg-gray-50",
                            unwrappedEntry.status === 'invited' && "bg-blue-50"
                          )}>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                              {rang ? `#${rang}` : '—'}
                            </td>
                            <td className="px-3 py-3 min-w-[12rem]">
                              <div className="text-sm font-medium text-gray-900">
                                {unwrappedEntry.kid?.prenom} {unwrappedEntry.kid?.nom}
                              </div>
                              {situation && <SituationSemaine situation={situation} />}
                            </td>
                            <td className="px-3 py-3 min-w-[12rem] text-sm">
                              <div className="font-medium text-gray-900">
                                {unwrappedEntry.parent?.prenom} {unwrappedEntry.parent?.nom}
                              </div>
                              <div className="mt-0.5 whitespace-nowrap text-gray-900">
                                <Telephone numero={unwrappedEntry.parent?.telephone} />
                              </div>
                              {unwrappedEntry.parent?.email && (
                                <a
                                  href={`mailto:${unwrappedEntry.parent.email}`}
                                  className="block break-all text-xs text-gray-500 hover:underline"
                                >
                                  {unwrappedEntry.parent.email}
                                </a>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                              {new Date(unwrappedEntry.created_at).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-3 py-3">
                              <span className={clsx(
                                "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                                getWaitingListStatusColor(unwrappedEntry)
                              )}>
                                <Clock className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                                {getWaitingListStatusText(unwrappedEntry)}
                              </span>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-3">
                                {unwrappedEntry.status === 'waiting' && (
                                  <button
                                    onClick={() => handleOfferSeat(unwrappedEntry.id)}
                                    disabled={isProcessingEntry === unwrappedEntry.id}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Envoyer au parent l'e-mail « une place est disponible »"
                                  >
                                    {isProcessingEntry === unwrappedEntry.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Mail className="h-4 w-4" />
                                    )}
                                  </button>
                                )}

                                {estActive(unwrappedEntry.status) && (
                                  <button
                                    onClick={() => ouvrirInscription(unwrappedEntry, session, situation)}
                                    disabled={isProcessingEntry === unwrappedEntry.id}
                                    className="inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-700 hover:bg-teal-100"
                                    title="Inscrire l'enfant au nom du parent et lui envoyer la facture"
                                  >
                                    <UserPlus className="h-3.5 w-3.5" />
                                    Inscrire
                                  </button>
                                )}

                                {estActive(unwrappedEntry.status) && (
                                  <button
                                    onClick={() => handleCancelWaitingList(unwrappedEntry.id)}
                                    disabled={isProcessingEntry === unwrappedEntry.id}
                                    className="text-red-600 hover:text-red-900"
                                    title="Retirer de la liste d'attente"
                                  >
                                    {isProcessingEntry === unwrappedEntry.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <X className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {aInscrire && (
        <InscriptionDepuisAttenteModal
          inscription={aInscrire}
          onClose={() => setAInscrire(null)}
          onInscrit={() => {
            fetchActivities();
            setRelectureInscriptions((n) => n + 1);
          }}
        />
      )}
    </div>
  );
};

export default AdminWaitingListPage;
